import {
  menu,
  popFilterableSimpleMenu,
  popupTargetFromElement,
} from '@blocksuite/affine-components/context-menu';
import { SignalWatcher, WithDisposable } from '@blocksuite/global/lit';
import { AddCursorIcon } from '@blocksuite/icons/lit';
import { ShadowlessElement } from '@blocksuite/std';
import { signal } from '@preact/signals-core';
import { css, nothing } from 'lit';
import { property } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { html } from 'lit/static-html.js';

import { DATA_SOURCE_KANBAN_GROUP_CARD_RENDER_LIMIT } from '../../../core/data-source/window.js';
import { GroupTitle } from '../../../core/group-by/group-title.js';
import type { Group } from '../../../core/group-by/trait.js';
import { dataViewI18nT } from '../../../core/i18n.js';
import { dragHandler } from '../../../core/utils/wc-dnd/dnd-context.js';
import { resolveKanbanVisibleCards } from '../card-render-window.js';
import type { MobileKanbanViewUILogic } from './kanban-view-ui-logic.js';

const styles = css`
  mobile-kanban-group {
    width: 260px;
    flex-shrink: 0;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
  }

  .mobile-group-header {
    height: 32px;
    padding: 6px 4px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    overflow: hidden;
  }

  .mobile-group-body {
    margin-top: 4px;
    display: flex;
    flex-direction: column;
    padding: 0 4px;
    gap: 12px;
  }

  .mobile-add-card {
    display: flex;
    align-items: center;
    padding: 4px;
    border-radius: 4px;
    font-size: var(--data-view-cell-text-size);
    line-height: var(--data-view-cell-text-line-height);
    color: var(--streamlain-v2-text-secondary);
  }

  .mobile-card-overflow {
    border: 1px solid var(--streamlain-v2-layer-insideBorder-border);
    border-radius: 6px;
    color: var(--streamlain-v2-text-secondary);
    font-size: var(--data-view-cell-text-size);
    line-height: var(--data-view-cell-text-line-height);
    padding: 8px;
    text-align: center;
    cursor: pointer;
    user-select: none;
    background: color-mix(
      in srgb,
      var(--streamlain-v2-layer-background-primary) 88%,
      transparent
    );
  }

  .mobile-card-overflow:hover {
    background: var(--streamlain-v2-layer-background-hoverOverlay);
    color: var(--streamlain-v2-text-primary);
  }
`;

export class MobileKanbanGroup extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  static override styles = styles;

  /**
   * Per-cell card render window — mirrors the PC group: starts at the
   * policy sample, tapping "+N more" pulls in one more policy step for
   * this cell only.
   */
  private readonly cardRenderLimit$ = signal(
    DATA_SOURCE_KANBAN_GROUP_CARD_RENDER_LIMIT
  );

  private readonly showMoreCards = () => {
    this.cardRenderLimit$.value += DATA_SOURCE_KANBAN_GROUP_CARD_RENDER_LIMIT;
  };

  private readonly clickAddCard = () => {
    this.view.addCard('end', this.group.key, this.rowKey);
    this.requestUpdate();
  };

  private readonly clickAddCardInStart = () => {
    this.view.addCard('start', this.group.key, this.rowKey);
    this.requestUpdate();
  };

  private readonly clickGroupOptions = (e: MouseEvent) => {
    const ele = e.currentTarget as HTMLElement;
    popFilterableSimpleMenu(popupTargetFromElement(ele), [
      menu.group({
        items: [
          menu.action({
            name: dataViewI18nT('kanban.ungroup'),
            hide: () => this.group.value == null,
            select: () => {
              this.group.rows.forEach(row => {
                this.group.manager.removeFromGroup(row.rowId, this.group.key);
              });
              this.requestUpdate();
            },
          }),
          menu.action({
            name: dataViewI18nT('kanban.deleteLoadedCards'),
            select: () => {
              this.view.rowsDelete(this.group.rows.map(row => row.rowId));
              this.requestUpdate();
            },
          }),
        ],
      }),
    ]);
  };

  override render() {
    const rowMap = this.view.group2Trait?.groupDataMap$.value;
    const rowRowIds = this.rowKey
      ? rowMap?.[this.rowKey]?.rows.map(r => r.rowId)
      : undefined;
    const { visibleCards, hiddenCount } = resolveKanbanVisibleCards(
      this.group.rows,
      rowRowIds,
      this.cardRenderLimit$.value
    );
    return html`
      <div class="mobile-group-header" ${dragHandler(this.group.key)}>
        ${GroupTitle(this.group, {
          readonly: this.view.readonly$.value,
          clickAdd: this.clickAddCardInStart,
          clickOps: this.clickGroupOptions,
        })}
      </div>
      <div class="mobile-group-body">
        ${repeat(
          visibleCards,
          row => row.rowId,
          row => {
            return html`
              <mobile-kanban-card
                data-card-id="${row.rowId}"
                data-row-key="${this.rowKey}"
                .groupKey="${this.group.key}"
                .rowKey="${this.rowKey}"
                .cardId="${row.rowId}"
                .kanbanViewLogic="${this.kanbanViewLogic}"
              ></mobile-kanban-card>
            `;
          }
        )}
        ${hiddenCount > 0
          ? html`<div
              class="mobile-card-overflow"
              role="button"
              title="${dataViewI18nT('kanban.showMoreCardsTitle')}"
              data-hidden-kanban-card-count="${hiddenCount}"
              @click="${this.showMoreCards}"
            >
              ${dataViewI18nT('kanban.showMoreCards', { count: hiddenCount })}
            </div>`
          : nothing}
        ${this.view.readonly$.value
          ? nothing
          : html` <div class="mobile-add-card" @click="${this.clickAddCard}">
              <div
                style="margin-right: 4px;width: 16px;height: 16px;display:flex;align-items:center;"
              >
                ${AddCursorIcon()}
              </div>
              Add
            </div>`}
      </div>
    `;
  }

  @property({ attribute: false })
  accessor group!: Group;

  @property({ attribute: false })
  accessor rowKey!: string;

  @property({ attribute: false })
  accessor kanbanViewLogic!: MobileKanbanViewUILogic;

  get view() {
    return this.kanbanViewLogic.view;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mobile-kanban-group': MobileKanbanGroup;
  }
}
